import { forwardRef } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { LogIn, LogOut, FileText } from "lucide-react";
import { fullName, qrValue, DAY_LABELS } from "@/lib/demo/utils";

import { useI18n } from "@/lib/i18n/i18n";

export const CredentialCard = forwardRef(({ participant: p }, qrRef) => {
  const { t } = useI18n();
  return (
  <div className="relative overflow-hidden rounded-[32px] border border-slate-200 bg-white shadow-[0_40px_90px_-40px_rgba(15,23,42,0.45)]" data-testid="credential-card">
    <div className="flex items-center justify-between bg-upv px-7 py-5 text-white">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.25em] text-white/70">{t("Foro de Empleo")}</p>
        <p className="font-display text-lg font-bold">UPV 2027</p>
      </div>
      <span className="font-display text-2xl font-extrabold tracking-tight">UPV</span>
    </div>
    <div className="px-7 pt-7">
      <p className="font-display text-2xl font-bold uppercase leading-tight tracking-tight sm:text-3xl" data-testid="credential-name">{fullName(p)}</p>
      <p className="mt-2 text-slate-600" data-testid="credential-titulacion">{t(p.titulacion)}</p>
      <p className="text-sm font-bold tracking-wider text-upv" data-testid="credential-centro">{p.centro}</p>
    </div>
    <div className="px-7 py-6">
      <div ref={qrRef} className="rounded-3xl border-2 border-slate-900/10 bg-white p-4" data-testid="credential-qr">
        <QRCodeCanvas value={qrValue(p.token)} size={600} level="M" marginSize={1} fgColor="#0F172A" style={{ width: "100%", height: "auto" }} />
      </div>
      <p className="mt-5 text-center font-mono text-lg font-bold tracking-[0.18em] text-slate-900" data-testid="credential-token">{p.token}</p>
    </div>
    <div className="relative h-6">
      <span className="absolute -left-3 top-0 h-6 w-6 rounded-full bg-slate-50" />
      <span className="absolute -right-3 top-0 h-6 w-6 rounded-full bg-slate-50" />
      <div className="mx-8 border-t-2 border-dashed border-slate-200 pt-3" />
    </div>
    <div className="flex items-center justify-between px-7 pb-6 text-sm">
      <span className="font-mono font-bold tracking-wider">{t("12–13 MAYO 2027")}</span>
      <span className="text-slate-500">València</span>
    </div>
  </div>
  );
});
CredentialCard.displayName = "CredentialCard";

export const ParticipantActivity = ({ items }) => {
  const { t } = useI18n();
  const rows = (items || []).map((it) => ({
    ...it,
    icon: it.kind === "entry" ? LogIn : it.kind === "exit" ? LogOut : FileText,
    label: t(it.kind === "entry" ? "ENTRADA" : it.kind === "exit" ? "SALIDA" : it.origin === "post_event_online" ? "CV ENVIADO" : "CV COMPARTIDO"),
    cv: it.kind === "cv",
    dayLabel: DAY_LABELS[it.day] ? t(DAY_LABELS[it.day]) : it.date,
  }));
  return (
    <div className="rounded-[28px] border border-slate-200 bg-white p-6" data-testid="participant-activity">
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">{t("Tu actividad en el Foro")}</p>
      {items === null ? <p className="mt-4 text-sm text-slate-400">{t("Cargando…")}</p> : rows.length === 0 ? (
        <p className="mt-4 text-sm text-slate-500" data-testid="participant-activity-empty">{t("Aún no hay lecturas. Tu historial aparecerá aquí cuando escanees tu QR.")}</p>
      ) : (
        <ul className="mt-4 space-y-2">
          {rows.map((it) => (
            <li key={it.id} className="flex items-center gap-4 rounded-xl bg-slate-50 px-4 py-3" data-testid={`participant-activity-item-${it.id}`}>
              <span className="w-12 font-mono text-sm font-bold">{it.time}</span>
              <it.icon className={`h-4 w-4 ${it.cv ? "text-upv" : "text-slate-500"}`} />
              <span className="text-xs font-bold tracking-wider">{it.label}</span>
              <span className="truncate text-xs text-slate-500">{t(it.sub)} · {it.dayLabel}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
